import * as React from 'react';

//utils
import styles from './premiumWrapper.module.scss';
import SVG from 'react-inlinesvg';
import parse from 'html-react-parser';

//redux
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Dispatch } from 'redux';

//redux states
import { RootState } from '../../../../store';
import { LoginState } from '../../../../store/login/reducer';
import { PremiumInfoState } from '../../../../store/premium/reducer';
import { tipsterActions } from '../../../../store/tipster/actions';
import { TipsterState } from '../../../../store/tipster/reducer';

//tr
import { useTranslation } from 'react-i18next';
import { tr } from '../../../../utils/translations/translator';

//components
import { BounceLoader } from 'react-spinners';
import { usePremiumProduct } from '../__hooks__/usePremiumProduct';
import { premiumInfoActions } from '../../../../store/premium/actions';
import { ProductContainer } from '../../shared/components/ProductContainer/ProductContainer';
import { Product } from '../../../PaymentGateway/interfaces/Product';
import { useTipsterRouteInfoContext } from '../../../__providers__/TipsterRouteInfoContext';

//assets
import { PremiumProduct } from '../interfaces/PremiumProduct';
import { ShopContext } from '../../shared/__providers__/ShopContext';
import { ProductContext } from '../../../PaymentGateway/__providers__/ProductContext';
import { NotAvailableScreen } from '../../shared/components/NotAvailableScreen';
import { ShopHeader } from '../../shared/components/ShopHeader';
import { loginActions } from '../../../../store/login/actions';
import { StoreTypesReversed } from '../../../PaymentGateway/interfaces/store_types';
import { PaymentGatewayConnected } from '../../../PaymentGateway/PaymentGateway';
import { usePaymentStageContext } from '../../../PaymentGateway/__providers__/PaymentStageContext';

export interface PremiumWrapperProps extends RouteComponentProps {
  requestPublicPremiumInfo: (tipster: string) => void;
  getTipsterInfo: (tipster_id: string) => any;
  premiumInfoState: PremiumInfoState;
  loginState: LoginState;
  tipsterState: TipsterState;
  requestLogout: () => void;
}

export const PremiumWrapper: React.FC<PremiumWrapperProps> = ({
  requestPublicPremiumInfo,
  getTipsterInfo,
  tipsterState,
  premiumInfoState,
  loginState,
  requestLogout,
}) => {
  const { t } = useTranslation();
  const { tipsterID, tipsterSlug } = useTipsterRouteInfoContext();
  const { setSelectedProductNum } = usePaymentStageContext();
  const [product, setProduct] = React.useState(
    new Product({
      id: '',
      name: '',
      price: { amount: 0, currency: 'EUR', decimals: 2 },
    }),
  );
  const { shop, isUnavailable, isLoading, handleSelectProduct } =
    usePremiumProduct({
      premiumInfoState,
      loginState,
      tipsterState,
      requestPublicPremiumInfo,
      getTipsterInfo,
      tipsterID,
    });

  React.useEffect(() => {
    if (shop) {
      setProduct(
        new Product({
          id: shop.selectedProduct.plan.id,
          price: shop.selectedProduct.price,
          name: shop.selectedProduct.template.code,
          discount: shop.selectedProduct.priceWithDiscount,
        }),
      );
    }
  }, [shop, shop?.selectedProduct]);

  return (
    <>
      {isLoading ? (
        <div className={styles.loadingWrapper}>
          <div className={styles.loading}>
            <BounceLoader color={'#27de81'} size={25} />
            <label>{tr(t, 'loading')} ...</label>
          </div>
        </div>
      ) : isUnavailable || !shop ? (
        <NotAvailableScreen
          title={`${
            tipsterState.data ? tipsterState.data.tipster.name : tipsterSlug
          } ${tr(t, 'no-plans-available')}`}
        />
      ) : (
        <>
          <ShopContext.Provider value={{ shop }}>
            <ShopHeader
              product={shop.selectedProduct}
              store={StoreTypesReversed.Premium}
              tipsterInfo={shop.tipsterInfo}
              isTipster={loginState.user?.user.is_tipster}
            ></ShopHeader>
            <ProductContext.Provider
              value={{
                product,
              }}
            >
              <ProductContainer>
                <div className={styles.container}>
                  <div className={styles.card}>
                    <div className={styles.header}>
                      {!!shop.selectedProduct.totalPlanDays ? (
                        <p className={styles.remainingDays}>
                          {parse(
                            t('remaining_days', {
                              days: `${shop.selectedProduct.totalPlanDays} ${tr(
                                t,
                                'days',
                              )}`,
                            }),
                          )}
                        </p>
                      ) : (
                        <p>{parse(t('premium_info_header'))}</p>
                      )}
                    </div>

                    <div className={styles.body}>
                      <div className={styles.premiumProducts}>
                        {shop.products.map(
                          (product: PremiumProduct, index: number) => {
                            return (
                              <div
                                className={styles.clickableBundle}
                                onClick={() => {
                                  handleSelectProduct(product);
                                  setSelectedProductNum(index);
                                }}
                                key={product.plan.id}
                              >
                                {product.render()}
                              </div>
                            );
                          },
                        )}
                      </div>

                      <div className={styles.premiumDaysInfo}>
                        <span className={styles.totalDays}>
                          {tr(t, 'in_total_you_will_have')}{' '}
                          <span className={styles.value}>
                            {shop.selectedProduct.totalPlanDays +
                              shop.selectedProduct.plan.duration_days}{' '}
                            {tr(t, 'days')}
                          </span>
                        </span>

                        <span className={styles.expirationDate}>
                          {tr(t, 'until')}{' '}
                          {shop.selectedProduct.afterPurchaseExpirationDate.format(
                            'DD Month',
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <PaymentGatewayConnected
                  shop={shop}
                  storeType={StoreTypesReversed.Premium}
                ></PaymentGatewayConnected>
              </ProductContainer>
              {/* <PaymentMethods
                store="PREMIUM"
                product={
                  new Product({
                    id: shop.selectedProduct.plan.id,
                    name: shop.selectedProduct.template.code,
                    price: shop.selectedProduct.price,
                    discount: shop.selectedProduct.priceWithDiscount,
                  })
                }
              /> */}
            </ProductContext.Provider>
          </ShopContext.Provider>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  premiumInfoState: state.premiumInfo,
  loginState: state.login,
  tipsterState: state.tipster,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  requestPublicPremiumInfo: (tipster_id: string) => {
    dispatch(premiumInfoActions.publicPremiumInfoRequestAction(tipster_id));
  },
  getTipsterInfo: (tipster_id: string) =>
    dispatch(tipsterActions.tipsterRequestAction(tipster_id)),
  requestLogout: () => {
    dispatch(loginActions.logoutRequestAction());
  },
});

export const PremiumWrapperConnected = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PremiumWrapper),
);
